<template>
  <div class="xy-wave-progress" ref="wrapRef">
    <canvas ref="canvasRef" class="xy-wave-progress__canvas"></canvas>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { info, defaultProps } from './config'

defineOptions({ name: info.name })
const props = defineProps(defaultProps)

const wrapRef = ref(null)
const canvasRef = ref(null)

const ratio = computed(() => {
  const raw = props.maxValue ? props.value / props.maxValue : 0
  if (!isFinite(raw) || isNaN(raw)) return 0
  return Math.min(1, Math.max(0, raw))
})

let ctx = null
let rafId = 0
let resizeObserver = null
let w = 0
let h = 0
let dpr = 1
let phase = 0

const clamp = (n, min, max) => Math.min(max, Math.max(min, n))

const toPxNumber = (val) => {
  if (val === null || val === undefined) return null
  if (typeof val === 'number') return isFinite(val) ? val : null
  if (typeof val === 'string') {
    if (!val) return null
    if (val === 'auto') return null
    const n = parseFloat(val)
    return isFinite(n) ? n : null
  }
  return null
}

const initCanvasSize = () => {
  const el = wrapRef.value
  const canvas = canvasRef.value
  if (!el || !canvas) return

  const contentWeight = window.innerWidth || 750
  const ratio = contentWeight / 750

  // 优先用节点配置尺寸（编辑器里更可靠），再兜底用 DOM 实测
  const styleW = toPxNumber(props.element?.style?.width * ratio)
  const styleH = toPxNumber(props.element?.style?.height * ratio)
  const rect = el.getBoundingClientRect()
  const nextW = Math.max(1, Math.floor(styleW || rect.width))
  const nextH = Math.max(1, Math.floor(styleH || rect.height))
  if (nextW === w && nextH === h && ctx) return

  w = nextW
  h = nextH
  dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1))

  canvas.width = Math.floor(w * dpr)
  canvas.height = Math.floor(h * dpr)
  canvas.style.width = `${w}px`
  canvas.style.height = `${h}px`

  ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }
}

const draw = () => {
  if (!ctx) return

  const r = ratio.value
  const padding = 0
  const innerX = padding
  const innerY = padding
  const innerW = w - padding * 2
  const innerH = h - padding * 2

  ctx.clearRect(0, 0, w, h)

  // 背景
  ctx.fillStyle = props.backgroundColor || 'transparent'
  ctx.fillRect(innerX, innerY, innerW, innerH)

  // 进度边界：0 或 >=100% 时直接纯色填充
  if (r >= 1) {
    ctx.fillStyle = props.progressColor
    ctx.fillRect(innerX, innerY, innerW, innerH)
    return
  }
  if (r <= 0) return

  // 波纹参数（和尺寸相关，避免小尺寸过密）
  const baseAmp = Math.min(innerW, innerH) * 0.04
  const amp = clamp(baseAmp, 2, 14)
  const waveLen = clamp(Math.min(innerW, innerH) * 0.9, 40, 220)

  ctx.fillStyle = props.progressColor

  if (props.direction === 'horizontal') {
    // 水平向右填充：从左往右增长
    const fillW = innerW * r
    const boundaryX = innerX + fillW

    ctx.beginPath()
    // 从顶部边界开始绘制波线（沿 y 方向变化）
    const startY = innerY
    ctx.moveTo(boundaryX + amp * Math.sin(phase), startY)
    const step = 2
    for (let y = 0; y <= innerH; y += step) {
      const yy = innerY + y
      const offset = amp * Math.sin((yy / waveLen) * Math.PI * 2 + phase)
      ctx.lineTo(boundaryX + offset, yy)
    }
    // 左侧闭合，填充到最左边
    ctx.lineTo(innerX, innerY + innerH)
    ctx.lineTo(innerX, innerY)
    ctx.closePath()
    ctx.fill()
  } else {
    // 垂直向上填充：从底往上增长
    const fillH = innerH * r
    const boundaryY = innerY + (innerH - fillH)

    ctx.beginPath()
    const startX = innerX
    ctx.moveTo(startX, boundaryY + amp * Math.sin(phase))
    const step = 2
    for (let x = 0; x <= innerW; x += step) {
      const xx = innerX + x
      const offset = amp * Math.sin((xx / waveLen) * Math.PI * 2 + phase)
      ctx.lineTo(xx, boundaryY + offset)
    }
    // 底部闭合，填充到最底边
    ctx.lineTo(innerX + innerW, innerY + innerH)
    ctx.lineTo(innerX, innerY + innerH)
    ctx.closePath()
    ctx.fill()
  }
}

const tick = () => {
  phase += 0.08
  draw()
  rafId = window.requestAnimationFrame(tick)
}

const start = async () => {
  await updateLoop()
}

const stop = () => {
  if (rafId) window.cancelAnimationFrame(rafId)
  rafId = 0
}

const updateLoop = async () => {
  await nextTick()
  initCanvasSize()
  const r = ratio.value
  const shouldAnimate = props.animate && r > 0 && r < 1
  if (shouldAnimate) {
    if (!rafId) rafId = window.requestAnimationFrame(tick)
  } else {
    stop()
    // 关闭动画时绘制固定水波线（锁定相位）
    if (!props.animate) phase = 0
    draw()
  }
}

watch(
  () => [props.value, props.maxValue, props.backgroundColor, props.progressColor, props.direction, props.animate],
  () => {
    updateLoop()
  },
  { immediate: true }
)

watch(
  () => [props.element?.style?.width, props.element?.style?.height],
  () => {
    updateLoop()
  }
)

onMounted(() => {
  start()
  if (wrapRef.value) {
    resizeObserver = new ResizeObserver(() => {
      initCanvasSize()
      updateLoop()
    })
    resizeObserver.observe(wrapRef.value)
  }
})

onBeforeUnmount(() => {
  stop()
  if (resizeObserver) resizeObserver.disconnect()
})
</script>

<style lang="scss" scoped>
.xy-wave-progress {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.xy-wave-progress__canvas {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
